/**
 * Stellar parallax: distance from the annual shift of a star against the background.
 *
 * A star at distance d shows a parallax angle p when seen from the ends of a baseline of
 * one astronomical unit, so d = 1 AU / tan p. For the tiny angles of real stars, tan p ≈ p
 * and a parallax in arcseconds gives the distance in parsecs directly: d(pc) = 1 / p(″).
 * That is the definition of the parsec.
 */

import { PARSEC } from "@/lib/tool-kit/astronomy/distance";
import {
  ASTRONOMICAL_UNIT,
  LIGHT_YEAR,
} from "@/lib/tool-kit/physics-fields/constants";
import type { UnitDef } from "@/lib/tool-kit/physics-fields/units";

/** Parallax units as a size in arcseconds. */
export const PARALLAX_UNITS: readonly UnitDef[] = [
  { id: "arcsec", symbol: "″", factor: 1 },
  { id: "mas", symbol: "mas", factor: 1e-3 },
  { id: "uas", symbol: "µas", factor: 1e-6 },
];

/** Distance units for the reverse direction, as a size in parsecs. */
export const PARALLAX_DISTANCE_UNITS: readonly UnitDef[] = [
  { id: "pc", symbol: "pc", factor: 1 },
  { id: "ly", symbol: "ly", factor: LIGHT_YEAR / PARSEC },
  { id: "kpc", symbol: "kpc", factor: 1000 },
  { id: "au", symbol: "AU", factor: ASTRONOMICAL_UNIT / PARSEC },
];

export interface ParallaxDistance {
  parsecs: number;
  lightYears: number;
  astronomicalUnits: number;
  kilometres: number;
  /** Distance modulus m − M = 5 log₁₀(d / 10 pc). */
  distanceModulus: number;
}

/** Distance for a parallax in arcseconds (must be positive). */
export function distanceFromParallax(arcseconds: number): ParallaxDistance {
  const parsecs = 1 / arcseconds;
  const metres = parsecs * PARSEC;
  return {
    parsecs,
    lightYears: metres / LIGHT_YEAR,
    astronomicalUnits: metres / ASTRONOMICAL_UNIT,
    kilometres: metres / 1000,
    distanceModulus: 5 * Math.log10(parsecs / 10),
  };
}

/** Parallax in arcseconds for a distance in parsecs (must be positive). */
export function parallaxFromParsecs(parsecs: number): number {
  return 1 / parsecs;
}

export interface DistanceRange {
  /** Nearest plausible distance, pc: from the parallax plus its uncertainty. */
  near: number;
  /** Farthest, pc; null when the parallax minus its uncertainty reaches zero or below. */
  far: number | null;
}

/** The one-sigma distance range for a parallax and its uncertainty (arcseconds). */
export function distanceRange(
  arcseconds: number,
  sigma: number
): DistanceRange {
  const low = arcseconds - sigma;
  return {
    near: 1 / (arcseconds + sigma),
    far: low > 0 ? 1 / low : null,
  };
}

export type ParallaxQuality = "precise" | "usable" | "poor";

/**
 * How far to trust 1/p for a given fractional parallax error σ/p: under 5% is precise,
 * to 20% usable with care, beyond that the inverse is badly biased and a prior is needed
 * (Bailer-Jones 2015, PASP 127, 994; Luri et al. 2018, A&A 616, A9).
 */
export function parallaxQuality(fractionalError: number): ParallaxQuality {
  if (fractionalError <= 0.05) {
    return "precise";
  }
  return fractionalError <= 0.2 ? "usable" : "poor";
}
